// Native maps, the kit side (docs/NATIVE-MAPS-CONTRACT.md §1, "The map looks complete"): the icon
// images every PopCart map registers, the basemap POI → PlaceCategory mapping, and the overlay specs
// for our own traffic (TrafficFeed) and places (PlacesFeed). Colours come from @popcart/tokens via
// mapPalette.json, which the icon builder (scripts/build-map-icons.mjs) reads too.
//
// Usage, both apps (MapLibre React Native v11):
//   <Map mapStyle={…}>
//     <Images images={mapIconImages} />
//     <GeoJSONSource id={mapTraffic.sourceId} data={trafficFeed}>
//       <Layer id={mapTraffic.layerId} type="line" beforeId={mapTraffic.beforeId} layout={mapTraffic.layout} paint={mapTraffic.paint} />
//     </GeoJSONSource>
//     <GeoJSONSource id={mapPlaces.sourceId} data={placesFeed}>
//       <Layer {...mapPlaces.layer} />
//     </GeoJSONSource>
//   </Map>
import type { ExpressionSpecification, LineLayerSpecification, SymbolLayerSpecification } from '@maplibre/maplibre-gl-style-spec';
import type { PlaceCategory, ProductLine, TrafficLevel } from '@popcart/core/v1';
import { tokens } from '@popcart/tokens';
import { mapIconAssets } from './icons';
import palette from './mapPalette.json';

/** A token path from mapPalette.json ('category.grill') → its hex. */
function colourAt(path: string): string {
  const hex = path.split('.').reduce<unknown>((o, k) => (o as Record<string, unknown> | undefined)?.[k], tokens.colour);
  if (typeof hex !== 'string') throw new Error(`mapPalette: no token colour at ${path}`);
  return hex;
}

/** Every PlaceCategory, in the contract's order. */
export const placeCategories = Object.keys(palette.poi) as PlaceCategory[];

/** Label colour per category: the icon's hue where it reads on Cream, else its darkest family token. */
export const placeLabelColours = Object.fromEntries(
  placeCategories.map((cat) => [cat, colourAt(palette.poi[cat].label)]),
) as Record<PlaceCategory, string>;

/**
 * The map's icon images, `require()`d PNGs at 1x/2x/3x (Metro picks the density and MapLibre gets
 * its scale, so `icon-size: 1` is the drawn size in points). Register on every map that uses the
 * PopCart styles or the overlays below: `<Images images={mapIconImages} />`.
 * Keys: `poi-<PlaceCategory>`, `popcart-fresh|grill|mart|express`, `oneway`.
 */
export const mapIconImages: Record<string, number> = mapIconAssets;

/**
 * OpenMapTiles `poi.class` → PlaceCategory. The schema folds most shop subclasses into `shop` and
 * supermarkets / markets into `grocery`; classes it doesn't fold come through as the OSM tag
 * (restaurant, bank, pharmacy…). Anything not listed is a 'landmark'.
 */
export const poiCategoryExpression = [
  'match',
  ['get', 'class'],
  ['restaurant', 'fast_food', 'food_court', 'bakery', 'ice_cream', 'bar', 'beer', 'pub', 'bbq', 'biergarten'],
  'food',
  ['cafe'],
  'cafe',
  ['grocery', 'supermarket', 'convenience', 'marketplace', 'greengrocer', 'butcher', 'alcohol_shop'],
  'grocery',
  ['shop', 'clothing_store', 'mall', 'department_store', 'hardware', 'doityourself', 'furniture', 'mobile_phone', 'shoes', 'jewelry', 'laundry', 'hairdresser', 'car', 'bicycle', 'music', 'florist', 'garden_centre'],
  'shop',
  ['hospital', 'pharmacy', 'doctors', 'dentist', 'clinic', 'veterinary', 'chemist'],
  'health',
  ['school', 'college', 'university', 'kindergarten', 'library'],
  'school',
  ['place_of_worship', 'church', 'chapel', 'mosque', 'temple', 'shrine'],
  'worship',
  ['bus', 'railway', 'aerialway', 'ferry_terminal', 'harbor', 'entrance', 'taxi', 'airport'],
  'transit',
  ['bank', 'atm', 'bureau_de_change', 'money_transfer'],
  'bank',
  ['fuel', 'charging_station'],
  'fuel',
  ['town_hall', 'police', 'fire_station', 'post', 'courthouse', 'embassy', 'prison'],
  'gov',
  ['park', 'playground', 'garden', 'zoo', 'golf', 'pitch', 'stadium', 'swimming', 'dog_park', 'campsite', 'nature_reserve'],
  'park',
  'landmark',
] as unknown as ExpressionSpecification;

/** A `match` over a PlaceCategory-valued expression → that category's label colour. */
export function placeLabelColourExpression(category: ExpressionSpecification): ExpressionSpecification {
  return ['match', category, ...placeCategories.flatMap((cat) => [cat, placeLabelColours[cat]]), placeLabelColours.landmark] as unknown as ExpressionSpecification;
}

// ── Traffic (TrafficFeed) ───────────────────────────────────────────────────────────────────
/** observed ÷ free-flow speed → level (shared with the API's tests). Non-finite reads as free. */
export function trafficLevelOf(ratio: number): TrafficLevel {
  if (!Number.isFinite(ratio)) return 'free';
  if (ratio >= 0.8) return 'free';
  if (ratio >= 0.55) return 'slow';
  if (ratio >= 0.3) return 'heavy';
  return 'jam';
}

const c = tokens.colour;
/** Green → amber → red → deep red, the colours everyone reads traffic in, all brand tokens. */
const trafficColours: Record<TrafficLevel, string> = {
  free: c.category.fresh,
  slow: c.functional.warning,
  heavy: c.category.grill,
  jam: c.functional.error,
};

/**
 * Our fleet's traffic: a line over the road fill and under every label (`beforeId: 'road-label'`,
 * the first label layer in both PopCart styles), a touch narrower than a main road so its edge
 * shows. 'typical' (historic) segments are drawn a little lighter than 'live' ones.
 */
export const mapTraffic: {
  sourceId: 'popcart-traffic';
  layerId: 'popcart-traffic';
  beforeId: 'road-label';
  colours: Record<TrafficLevel, string>;
  layout: NonNullable<LineLayerSpecification['layout']>;
  paint: NonNullable<LineLayerSpecification['paint']>;
} = {
  sourceId: 'popcart-traffic',
  layerId: 'popcart-traffic',
  beforeId: 'road-label',
  colours: trafficColours,
  layout: { 'line-cap': 'round', 'line-join': 'round' },
  paint: {
    'line-color': ['match', ['get', 'level'], 'slow', trafficColours.slow, 'heavy', trafficColours.heavy, 'jam', trafficColours.jam, trafficColours.free],
    'line-width': ['interpolate', ['exponential', 1.4], ['zoom'], 10, 1.5, 13, 2.5, 15, 4, 18, 9],
    'line-opacity': ['match', ['get', 'source'], 'typical', 0.75, 0.95],
  },
};

// ── Places (PlacesFeed) ─────────────────────────────────────────────────────────────────────
const LABEL_FONT = ['Noto Sans Regular'];
const LABEL_FONT_BOLD = ['Noto Sans Bold'];
const isShop: ExpressionSpecification = ['==', ['get', 'kind'], 'shop'];
const shopLines: ProductLine[] = ['fresh', 'grill', 'mart'];

/**
 * Our own places: PopCart shops as their line's pin (`popcart-<line>`), always shown from z13 and
 * never hidden by the basemap; rider landmarks as their category's icon (`poi-<category>`) from z15.
 * Names sit beside the icon and drop out when they'd collide. Closed shops are dimmed.
 */
export const mapPlaces = {
  sourceId: 'popcart-places',
  layerId: 'popcart-places',
  layer: {
    id: 'popcart-places',
    type: 'symbol',
    source: 'popcart-places',
    minzoom: 13,
    filter: ['any', isShop, ['>=', ['zoom'], 15]],
    layout: {
      'icon-image': [
        'case',
        ['all', isShop, ['in', ['get', 'line'], ['literal', shopLines]]],
        ['concat', 'popcart-', ['get', 'line']],
        ['concat', 'poi-', ['get', 'category']],
      ],
      'icon-anchor': ['case', isShop, 'bottom', 'center'],
      'icon-allow-overlap': true,
      'symbol-sort-key': ['case', isShop, 0, 1],
      'text-field': ['get', 'name'],
      'text-font': ['case', isShop, ['literal', LABEL_FONT_BOLD], ['literal', LABEL_FONT]],
      'text-size': ['case', isShop, 12, 11],
      'text-anchor': 'left',
      'text-offset': ['case', isShop, ['literal', [1.3, -1.6]], ['literal', [1.1, 0]]],
      'text-max-width': 8,
      'text-optional': true,
    },
    paint: {
      'icon-opacity': ['case', ['==', ['get', 'open'], false], 0.55, 1],
      'text-color': ['case', isShop, c.core.ink, placeLabelColourExpression(['get', 'category'])],
      'text-halo-color': c.core.cream,
      'text-halo-width': 1.5,
    },
  } as SymbolLayerSpecification,
} as const;
